/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles/shared-styles.js';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import searchStyles from './sh-search.lit.scss.js';
/**
 * @prop {String} label - Defines the placeholder text for the search box.
 * @prop {Array} searchArray - Defines the array of items that will appear to the user as suggestions as she/he starts typing. This array can be linked to a data set through data-binding. The way to do the data-binding will depend on your application's JS framework (e.g. Angular, React, Vue, ...). When multiline is enabled search-array to be given as array of objects (e.g.[{name:'jack',email:'jack@email.com'},{name:'john',email:'john@email.com'}]) and fields property can be set.
 * @attr {Array} search-array - Defines the array of items that will appear to the user as suggestions as she/he starts typing. This array can be linked to a data set through data-binding. The way to do the data-binding will depend on your application's JS framework (e.g. Angular, React, Vue, ...). When multiline is enabled search-array to be given as array of objects (e.g.[{name:'jack',email:'jack@email.com'},{name:'john',email:'john@email.com'}]) and fields property can be set.
 * @prop {Boolean} multiline - Default false. If set to `true`, multiline search will be enabled.
 * @prop {Array} fields - Defines key names for which autocomplete will appear e.g. ['name','email']. If not specified first two keys will be taken.
 * @prop {Boolean} wholeString - Default false. If set to `true`, search will be enabled for whole string instead of just first character in the search array.
 * @attr {Boolean} whole-string - Default false. If set to true, search will be enabled for whole string instead of just first character in the search array.
 * @slot empty - Takes Empty State as child. Will be displayed if the search suggestions are empty (value doesn't match any item in the search-array).
 * @prop {Boolean} disabled - Disables component when set.
 * @prop {String} value - Initial value of search field.
 * @prop {Boolean} noClear - If set, clear icon will not be displayed.
 * @attr {Boolean} no-clear - If set, clear icon will not be displayed.
 * @fires search - Dispatched when clicking on the search icon.
 * @fires noresult - Dispatched when no matching result found.
 * @fires clearsearch - Dispatched when clicking on clear icon.
 */
export class SHSearch extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      disabled: { type: Boolean, reflect: true },
      searchArray: { type: Array, reflect: true, attribute: 'search-array' },
      multiline: { type: Boolean, reflect: true },
      // readonly properties
      /** @ignore */
      _currentFocus: { type: Number },
      /** @ignore */
      _emptyResult: { type: Boolean },
      value: { type: String, reflect: true },
      fields: { type: Array, reflect: true },
      wholeString: { type: Boolean, reflect: true, attribute: 'whole-string' },
      noClear: { type: Boolean, reflect: true, attribute: 'no-clear' },
    };
  }

  static get styles() {
    return [searchStyles, sharedStyles];
  }
  render() {
    return html`
      <input
        type="text"
        .value="${this.value}"
        @input="${(e) => this.onInput(e)}"
        @keyup="${() => this.changeInput(this.value)}"
        @keydown="${(e) => this.keyEvents(e)}"
        id="searchBox"
        ?disabled="${this.disabled}"
        placeholder="${this.label}"
      />
      <sh-icon
        size="s"
        icon="search"
        ?disabled="${this.disabled}"
        button
        @click="${() => this._search()}"
      ></sh-icon>
      ${this.value && !this.disabled && !this.noClear
        ? html`
            <sh-icon
              icon="cancel"
              size="s"
              button
              class="closeIcon"
              id="close"
              ?disabled="${this.disabled}"
              @click="${() => this.clearInput()}"
            ></sh-icon>
          `
        : ''}
      <sh-overlay
        id="showSearch"
        class="showSearch"
        style="width:${getComputedStyle(this).width};"
        scroll-action="refit"
        @focus="${(e) => this.overlayFocusHandler(e)}"
        no-overlap
        horizontal-align="left"
        no-cancel-on-outside-click
      >
        ${!this.multiline
          ? html`
              ${this.searchArray.map(
                (item) => html`
                  ${this.computeSingleFilter(item)
                    ? html`
                        <div
                          @click="${(e) => this.selectItem(e)}"
                          class="itemList"
                          data-item="${item}"
                        >
                          <span class="name" data-item="${item}">${item}</span>
                        </div>
                      `
                    : ''}
                `
              )}
            `
          : ''}
        ${this.multiline
          ? html`
              ${this.searchArray.map(
                (item) => html`
                  ${this.computeFilter(item)
                    ? html`
                        <div
                          @click="${(e) => this.selectItem(e)}"
                          class="itemList"
                          data-item="${item[this.fields[0]]}"
                        >
                          <div class="firstItem" data-item="${item[this.fields[0]]}">
                            <span class="name" data-item="${item[this.fields[0]]}"
                              >${item[this.fields[0]]}</span
                            >
                          </div>
                          <div
                            class="secondItem"
                            data-item="${item[this.fields[0]]}"
                            data-second-item="${item[this.fields[1]]}"
                          >
                            <span class="name" data-item="${item[this.fields[0]]}"
                              >${item[this.fields[1]]}</span
                            >
                          </div>
                        </div>
                      `
                    : ''}
                `
              )}
            `
          : ''}
      </sh-overlay>
      ${this._emptyResult && this.children.length > 0
        ? html`
            <sh-overlay
              opened
              class="empty-wrapper"
              style="width:${getComputedStyle(this).width}"
              scroll-action="refit"
              no-overlap
              horizontal-align="left"
              no-cancel-on-outside-click
            >
              <slot name="empty"></slot>
            </sh-overlay>
          `
        : ''}
    `;
  }

  constructor() {
    super();
    this.searchArray = [];
    this.wholeString = false;
    this.value = '';
    this.label = 'Search';
    this._currentFocus = -1;
    this.fields = [];
  }
  connectedCallback() {
    super.connectedCallback();
    /** @ignore */
    this.clickListener = this.closeSearch.bind(this);
    document.body.addEventListener('click', this.clickListener);
    if (this.multiline && this.fields.length === 0 && this.searchArray.length > 0) {
      this.fields[0] = Object.keys(this.searchArray[0])[0];
      this.fields[1] = Object.keys(this.searchArray[0])[1];
    }
  }

  firstUpdated() {
    this._overlay = this.shadowRoot.querySelector('#showSearch');
    this.shadowRoot.querySelector('input').addEventListener('focus', () => {
      if (this.value) {
        this._overlay.open();
        if (this._overlay.querySelectorAll('.itemList').length === 0) {
          this._emptyResult = true;
        }
      } else {
        this._overlay.close();
        this._emptyResult = false;
      }
    });
    this.shadowRoot.querySelector('input').addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        this._search();
      }
    });
  }
  updated(changedProperties) {
    const listOfProperties = ['label', 'disabled', 'value', 'multiline', 'fields'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  disconnectedCallback() {
    document.body.removeEventListener('click', this.clickListener);
    super.disconnectedCallback();
  }

  closeSearch(e) {
    if (e.target === this) {
      return;
    } else {
      this._overlay.close();
      this._emptyResult = false;
    }
  }

  overlayFocusHandler(e) {
    e.preventDefault();
    this.shadowRoot.querySelector('input').focus();
  }

  onInput(e) {
    this.value = e.target.value;
  }

  changeInput(value) {
    if (this.value) {
      this._overlay.open();
    } else {
      this._overlay.close();
    }
    if (!this.multiline && this.value) {
      const itemList = this.shadowRoot.querySelectorAll('#showSearch .itemList');
      for (let i = 0; i < itemList.length; i++) {
        const fullStr = itemList[i].dataset.item;
        itemList[i].innerHTML = this.searchHighlight(fullStr, value);
      }
    } else {
      if (this.value) {
        this.multilineSearch();
      }
    }
    if (this._overlay.querySelectorAll('.itemList').length === 0) {
      if (this.value === '') {
        return;
      }
      dispatchCustomEvent(this, 'noresult');
      this._emptyResult = true;
    } else {
      this._emptyResult = false;
    }
  }

  multilineSearch() {
    const firstItemList = this._overlay.querySelectorAll('.itemList .firstItem');
    const secondItemList = this._overlay.querySelectorAll('.itemList .secondItem');
    for (let i = 0; i < firstItemList.length; i++) {
      this.findSearchedStr(firstItemList[i], firstItemList[i].dataset.item);
    }
    for (let i = 0; i < secondItemList.length; i++) {
      this.findSearchedStr(secondItemList[i], secondItemList[i].dataset.secondItem);
    }
  }
  findSearchedStr(item, itemStr) {
    const fullStr = itemStr;
    if (!this.wholeString && fullStr.toLowerCase().indexOf(this.value.toLowerCase()) !== -1) {
      item.innerHTML = this.searchHighlight(fullStr, this.value);
    } else if (this.wholeString && fullStr.toLowerCase().indexOf(this.value.toLowerCase()) !== -1) {
      item.innerHTML = this.searchHighlight(fullStr, this.value);
    } else {
      item.innerHTML = `<span class="name">${fullStr}</span>`;
    }
  }
  searchHighlight(fullStr, value) {
    let replaceHtml;
    if (value) {
      const matchStart = fullStr.toLowerCase().indexOf(value.toLowerCase());
      const matchEnd = matchStart + value.length - 1;
      const beforeMatch = fullStr.slice(0, matchStart);
      const matchText = fullStr.slice(matchStart, matchEnd + 1);
      const afterMatch = fullStr.slice(matchEnd + 1);
      replaceHtml = `${beforeMatch}<span class="searchText">${matchText}</span>${afterMatch}`;
    }
    return replaceHtml;
  }

  computeSingleFilter(inputStr) {
    let searchedString;
    if (this.wholeString) {
      searchedString = inputStr.toLowerCase().indexOf(this.value.toLowerCase()) !== -1;
    } else {
      searchedString = inputStr.toLowerCase().indexOf(this.value.toLowerCase()) === 0;
    }
    return searchedString;
  }

  computeFilter(inputStr) {
    let searchedString;
    if (this.wholeString) {
      searchedString =
        inputStr[this.fields[0]].toLowerCase().indexOf(this.value.toLowerCase()) !== -1 ||
        inputStr[this.fields[1]].toLowerCase().indexOf(this.value.toLowerCase()) !== -1;
    } else {
      searchedString =
        inputStr[this.fields[0]].toLowerCase().indexOf(this.value.toLowerCase()) === 0 ||
        inputStr[this.fields[1]].toLowerCase().indexOf(this.value.toLowerCase()) === 0;
    }
    return searchedString;
  }

  clearInput() {
    this.value = '';
    this._emptyResult = false;
    dispatchCustomEvent(this, 'clearsearch');
    this._overlay.close();
  }

  selectItem(e) {
    this.value = e.target.dataset.item;
    if (e.target.tagName === 'SPAN') {
      this.value = e.target.parentNode.dataset.item;
    } else {
      this.value = e.target.dataset.item;
    }
    this.shadowRoot.querySelector('input').value = this.value;
    this.changeInput(this.value);
    this._overlay.close();
    this.shadowRoot.querySelector('input').blur();
    this.clearActive();
    this._search();
  }
  clearActive() {
    const activeClass = this._overlay.getElementsByClassName('active');
    if (activeClass) {
      for (let i = 0; i < activeClass.length; i++) {
        activeClass[i].classList.remove('active');
      }
    }
    this._currentFocus = -1;
  }
  //keyboard events using up and down arrows to navigate between the search results
  keyEvents(e) {
    let itemlist;
    const arrowUpKey = 38;
    const arrowDownKey = 40;
    const enterKey = 13;
    itemlist = this.shadowRoot.querySelector('#showSearch');
    if (itemlist) {
      itemlist = itemlist.querySelectorAll('.itemList');
    }
    if (e.keyCode === arrowDownKey) {
      e.preventDefault();
      this._currentFocus++;
      this.addActive(itemlist);
    }
    if (e.keyCode === arrowUpKey) {
      e.preventDefault();
      this._currentFocus--;
      this.addActive(itemlist);
    }
    if (e.keyCode === enterKey) {
      e.preventDefault();
      if (this._currentFocus > -1 && itemlist.length > 0) {
        if (e.target.tagName === 'INPUT') {
          e.stopImmediatePropagation();
        }
        itemlist[this._currentFocus].click();
        this.blur();
      }
    }
  }
  addActive(itemlist) {
    if (!itemlist || this._emptyResult) {
      return;
    }
    this.removeActive(itemlist);
    if (this._currentFocus >= itemlist.length) {
      this._currentFocus = 0;
    }
    if (this._currentFocus < 0) {
      this._currentFocus = itemlist.length - 1;
    }
    itemlist[this._currentFocus].classList.add('active');
  }

  removeActive(itemlist) {
    for (let i = 0; i < itemlist.length; i++) {
      itemlist[i].classList.remove('active');
    }
  }

  _search() {
    // fire event on searching
    dispatchCustomEvent(this, 'search');
  }
}
customElements.define('sh-search', SHSearch);
